"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/auth-context";
import {
  startDevSession,
  updateDevSession,
  createActivityTracker,
  recordKeystroke,
  recordMouseEvent,
  type ActivityTracker,
} from "@/services/dev-session";

export function DevActivityTracker() {
  const { user, userProfile } = useAuth();
  const trackerRef = useRef<ActivityTracker | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const flushTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mouseThrottleRef = useRef<number>(0);

  const isAdmin =
    userProfile?.accountType === "superadmin" ||
    userProfile?.accountType === "admin" ||
    userProfile?.roles?.includes("admin") ||
    userProfile?.roles?.includes("superadmin");

  useEffect(() => {
    if (!user || !isAdmin) return;

    let mounted = true;

    async function initSession() {
      try {
        const storedSession = sessionStorage.getItem("bb-dev-session-id");
        let sId = storedSession;
        if (!sId) {
          sId = await startDevSession(
            user!.uid,
            userProfile?.displayName || user!.email || "Admin Developer",
            "manual",
            "Platform Dev & Admin Activity"
          );
          sessionStorage.setItem("bb-dev-session-id", sId);
        }

        if (!mounted) return;
        sessionIdRef.current = sId;
        trackerRef.current = createActivityTracker(sId);

        // Periodically sync keystrokes and duration to Firestore every 30 seconds
        flushTimerRef.current = setInterval(async () => {
          if (!sessionIdRef.current || !trackerRef.current) return;
          const tr = trackerRef.current;
          const activeSec = Math.max(1, Math.floor(tr.activeDurationMs / 1000));
          const costEur = (activeSec / 3600) * 150; // €150/hr rate

          try {
            await updateDevSession(sessionIdRef.current, {
              activeDurationSeconds: activeSec,
              keystrokesTotal: tr.keystrokesTotal,
              mouseEventsTotal: tr.mouseEventsTotal,
              activityBuckets: tr.buckets,
              costEur,
            });
          } catch (e) {
            console.error("Failed to sync dev session activity:", e);
          }
        }, 30_000);
      } catch (err) {
        console.error("Failed to initialize dev session:", err);
      }
    }

    initSession();

    // ── Keydown listener ──
    const handleKeyDown = () => {
      if (!trackerRef.current) return;
      trackerRef.current = recordKeystroke(trackerRef.current);
    };

    // ── Mouse & Touch listener (throttled to 1 event per 300ms) ──
    const handleMouse = () => {
      const now = Date.now();
      if (now - mouseThrottleRef.current < 300) return;
      mouseThrottleRef.current = now;
      if (!trackerRef.current) return;
      trackerRef.current = recordMouseEvent(trackerRef.current);
    };

    window.addEventListener("keydown", handleKeyDown, { passive: true });
    window.addEventListener("mousemove", handleMouse, { passive: true });
    window.addEventListener("click", handleMouse, { passive: true });

    return () => {
      mounted = false;
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousemove", handleMouse);
      window.removeEventListener("click", handleMouse);
    };
  }, [user, isAdmin, userProfile]);

  return null;
}
